import React, { useState, useEffect, useCallback } from 'react';
import { useAppSelector } from '../../store/hooks';
import strings from '../../utils/strings';
import { CText, CLoader, SEO } from '../../components/common';
import {
  FiUploadCloud,
  FiTrash2,
  FiTrendingUp,
  FiTrendingDown,
  FiActivity,
  FiShield,
  FiZap,
  FiPieChart,
  FiAlertTriangle,
  FiTarget,
  FiBarChart2,
  FiCheckCircle,
  FiLock,
  FiClock,
  FiCpu,
} from 'react-icons/fi';
import {
  fetchPortfolioHoldings,
  deletePortfolio,
  analyzePortfolio,
  PortfolioHolding,
  PortfolioSummary,
  PortfolioUploadInfo,
  PortfolioHealthCheckResult,
} from '../../api/portfolio';
import PortfolioUploadModal from './PortfolioUploadModal';
import PortfolioHealthCheck from './PortfolioHealthCheck';
import PortfolioStockAnalysis from './PortfolioStockAnalysis';

const formatINR = (value: number): string => {
  const abs = Math.abs(value);
  let formatted: string;
  if (abs >= 10000000) {
    formatted = (abs / 10000000).toFixed(2) + ' Cr';
  } else if (abs >= 100000) {
    formatted = (abs / 100000).toFixed(2) + ' L';
  } else {
    formatted = abs.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return (value < 0 ? '-' : '') + '\u20B9' + formatted;
};

type TabKey = 'holdings' | 'healthcheck' | 'analysis';

const tabs: { key: TabKey; label: string }[] = [
  { key: 'holdings', label: 'My Holdings' },
  { key: 'healthcheck', label: 'Health Score' },
  { key: 'analysis', label: 'AI Verdicts' },
];

const Portfolio: React.FC = () => {
  const colors = useAppSelector((state) => state.theme.theme);
  const [holdings, setHoldings] = useState<PortfolioHolding[]>([]);
  const [summary, setSummary] = useState<PortfolioSummary | null>(null);
  const [uploadInfo, setUploadInfo] = useState<PortfolioUploadInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState<TabKey>('holdings');

  // Health check state
  const [healthCheckResult, setHealthCheckResult] = useState<PortfolioHealthCheckResult | null>(null);
  const [healthCheckLoading, setHealthCheckLoading] = useState(false);
  const [healthCheckError, setHealthCheckError] = useState<string | null>(null);

  // Stock analysis state
  const [selectedStock, setSelectedStock] = useState<PortfolioHolding | null>(null);

  const loadHoldings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchPortfolioHoldings();
      setHoldings(data.holdings);
      setSummary(data.summary);
      setUploadInfo(data.upload);
    } catch (err) {
      console.error('Failed to fetch holdings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHoldings();
  }, [loadHoldings]);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete all portfolio data?')) return;
    try {
      await deletePortfolio();
      setHoldings([]);
      setSummary(null);
      setUploadInfo(null);
      setHealthCheckResult(null);
      setSelectedStock(null);
    } catch (err) {
      console.error('Failed to delete portfolio:', err);
    }
  };

  const handleRunHealthCheck = async () => {
    setHealthCheckLoading(true);
    setHealthCheckError(null);
    setActiveTab('healthcheck');
    try {
      const result = await analyzePortfolio();
      setHealthCheckResult(result);
    } catch (err: any) {
      console.error('Health check failed:', err);
      setHealthCheckError(err?.response?.data?.error || err?.message || 'Failed to analyze portfolio. Please try again.');
    } finally {
      setHealthCheckLoading(false);
    }
  };

  const handleAnalyzeStock = (h: PortfolioHolding) => {
    setSelectedStock(h);
    setActiveTab('analysis');
  };

  // Loading state
  if (loading) {
    return (
      <div
        className="p-4 md:p-6 lg:p-8 min-h-screen flex items-center justify-center"
        style={{ backgroundColor: colors.backgroundColor }}
      >
        <CLoader />
      </div>
    );
  }

  const isEmpty = !holdings.length;

  return (
    <div
      className="p-4 md:p-6 lg:p-8 min-h-screen pb-8"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <SEO
        title="Portfolio Doctor - Free AI Portfolio Health Check for Indian Investors"
        description="Upload your Zerodha, ICICI Direct, or Motilal Oswal holdings and get an AI-powered portfolio health score. Identify sector concentration risks, quality metrics, and get actionable rebalancing recommendations."
        canonical="/portfolio"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'Portfolio Doctor',
            applicationCategory: 'FinanceApplication',
            operatingSystem: 'Web',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'INR' },
            description: 'AI-powered portfolio health check for Indian investors. Upload broker holdings and get a health score, risk flags, sector analysis, and rebalancing recommendations.',
            featureList: [
              'Portfolio Health Score (0-100)',
              'Sector concentration analysis',
              'Risk flag detection',
              'Weighted average ROE, P/E, ROCE',
              'AI stock verdicts (HOLD/ADD/REDUCE)',
              'Rebalancing recommendations',
            ],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'HowTo',
            name: 'How to Check Your Portfolio Health',
            step: [
              { '@type': 'HowToStep', position: 1, name: 'Export holdings', text: 'Export your holdings as CSV or Excel from Zerodha, ICICI Direct, or Motilal Oswal.' },
              { '@type': 'HowToStep', position: 2, name: 'Upload the file', text: 'Upload the exported file to Portfolio Doctor.' },
              { '@type': 'HowToStep', position: 3, name: 'Get your health report', text: 'Receive an AI-powered health score with risk flags, sector analysis, and rebalancing advice.' },
            ],
          },
        ]}
      />
      <div className="lg:max-w-5xl lg:mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2.5">
            {React.createElement(FiActivity as any, { size: 22, color: colors.primary })}
            {isEmpty ? (
              <span className="text-2xl font-bold" style={{ color: colors.textColor }}>
                Portfolio Doctor
              </span>
            ) : (
              <h1 className="text-2xl font-bold m-0" style={{ color: colors.textColor }}>
                Portfolio Doctor
              </h1>
            )}
          </div>
          {!isEmpty && (
            <div className="flex gap-2">
              <button
                onClick={() => setShowUploadModal(true)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{ backgroundColor: colors.primary + '15', color: colors.primary }}
              >
                {React.createElement(FiUploadCloud as any, { size: 16 })}
                <span className="hidden sm:inline">{strings.reUploadHoldings}</span>
              </button>
              <button
                onClick={handleDelete}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{ backgroundColor: colors.alertColor + '15', color: colors.alertColor }}
              >
                {React.createElement(FiTrash2 as any, { size: 16 })}
              </button>
            </div>
          )}
        </div>

        {isEmpty ? (
          /* Empty State — Full Pitch Page */
          <div className="space-y-10">
            {/* A. Hero Section */}
            <div
              className="rounded-2xl px-6 py-12 md:py-16 text-center"
              style={{
                background: `linear-gradient(135deg, ${colors.gradient1} 0%, ${colors.gradient2} 50%, ${colors.gradient1} 100%)`,
              }}
            >
              <h1 className="text-2xl md:text-3xl font-bold mb-3 m-0" style={{ color: colors.white }}>
                {strings.uploadHoldings}
              </h1>
              <p className="text-sm md:text-base mb-6 mx-auto" style={{ color: 'rgba(255,255,255,0.8)', maxWidth: 480, margin: '0 auto 24px' }}>
                Upload your broker holdings and get a free AI-powered health score with risk flags, sector analysis, and actionable recommendations.
              </p>
              <button
                onClick={() => setShowUploadModal(true)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-base transition-all duration-200 hover:-translate-y-0.5"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  color: colors.gradient1,
                  boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                }}
              >
                {React.createElement(FiUploadCloud as any, { size: 20 })}
                Upload Holdings — It's Free
              </button>
              <p className="mt-3 text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
                Supports Zerodha, ICICI Direct, and Motilal Oswal
              </p>
            </div>

            {/* B. What Your Health Check Reveals */}
            <div>
              <h2 className="text-lg font-bold mb-4 m-0" style={{ color: colors.textColor }}>
                What Your Health Check Reveals
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { icon: FiShield, title: 'Health Score (0-100)', desc: 'One number for portfolio soundness' },
                  { icon: FiPieChart, title: 'Sector Breakdown', desc: 'Visual chart of sector allocation' },
                  { icon: FiAlertTriangle, title: 'Risk Flags', desc: 'Auto-detects concentration >35%, top-3 dominance >60%, low-ROE' },
                  { icon: FiBarChart2, title: 'Quality Metrics', desc: 'Weighted avg ROE, P/E, ROCE' },
                  { icon: FiZap, title: 'AI Stock Verdicts', desc: 'HOLD/ADD/REDUCE for each stock' },
                  { icon: FiTarget, title: 'Rebalancing Advice', desc: 'Actionable improvement steps' },
                ].map((card) => (
                  <div
                    key={card.title}
                    className="rounded-xl p-4 border"
                    style={{ backgroundColor: colors.inputBackground, borderColor: colors.borderColor }}
                  >
                    <div className="flex items-center gap-2.5 mb-2">
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: colors.primary + '12' }}
                      >
                        {React.createElement(card.icon as any, { size: 16, color: colors.primary })}
                      </div>
                      <h3 className="text-sm font-semibold m-0" style={{ color: colors.textColor }}>
                        {card.title}
                      </h3>
                    </div>
                    <CText type="r12" color={colors.grayScale500}>
                      {card.desc}
                    </CText>
                  </div>
                ))}
              </div>
            </div>

            {/* C. How It Works */}
            <div>
              <h2 className="text-lg font-bold mb-4 m-0" style={{ color: colors.textColor }}>
                How It Works
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { step: '1', title: 'Export from your broker', desc: 'Download your holdings as CSV or Excel from your broker app.' },
                  { step: '2', title: 'Upload the file', desc: 'Drag and drop or browse — we parse it instantly.' },
                  { step: '3', title: 'Get your health report', desc: 'AI scores your portfolio and flags risks in under 60 seconds.' },
                ].map((item) => (
                  <div
                    key={item.step}
                    className="rounded-xl p-4 border"
                    style={{ backgroundColor: colors.inputBackground, borderColor: colors.borderColor }}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <span
                        className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
                        style={{ backgroundColor: colors.primary, color: '#fff' }}
                      >
                        {item.step}
                      </span>
                      <h3 className="text-sm font-semibold m-0" style={{ color: colors.textColor }}>
                        {item.title}
                      </h3>
                    </div>
                    <CText type="r12" color={colors.grayScale500}>
                      {item.desc}
                    </CText>
                  </div>
                ))}
              </div>
            </div>

            {/* D. Trust Badges */}
            <div className="flex flex-wrap items-center justify-center gap-6">
              {[
                { icon: FiLock, text: 'Your data stays private' },
                { icon: FiClock, text: 'Analysis in under 60 seconds' },
                { icon: FiCpu, text: 'Powered by AI + real financials' },
              ].map((badge) => (
                <div key={badge.text} className="flex items-center gap-2">
                  {React.createElement(badge.icon as any, { size: 14, color: colors.grayScale400 })}
                  <CText type="r12" color={colors.grayScale400}>{badge.text}</CText>
                </div>
              ))}
            </div>

            {/* E. Bottom CTA */}
            <div className="text-center pb-4">
              <button
                onClick={() => setShowUploadModal(true)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-base transition-all duration-200 hover:-translate-y-0.5"
                style={{
                  background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)`,
                  color: '#fff',
                  boxShadow: colors.shadowMedium,
                }}
              >
                {React.createElement(FiCheckCircle as any, { size: 18 })}
                Check Your Portfolio Health
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Pill Tabs */}
            <div
              className="flex rounded-full p-1 mb-5"
              style={{
                backgroundColor: colors.inputBackground,
                boxShadow: colors.shadowSoft,
              }}
            >
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  className="flex-1 py-2.5 rounded-full text-sm font-medium transition-all duration-200"
                  style={{
                    backgroundColor: activeTab === tab.key ? colors.primary : 'transparent',
                    color: activeTab === tab.key ? colors.white : colors.grayScale400,
                    boxShadow: activeTab === tab.key ? colors.shadowMedium : 'none',
                  }}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Holdings Tab */}
            {activeTab === 'holdings' && (
              <>
                {/* Summary Card */}
                {summary && (
                  <div
                    className="rounded-2xl p-5 md:p-6 mb-5"
                    style={{
                      background: `linear-gradient(135deg, ${colors.gradient1} 0%, ${colors.gradient2} 50%, ${colors.gradient1} 100%)`,
                      color: colors.white,
                    }}
                  >
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <CText type="r12" color="rgba(255,255,255,0.7)" style={{ marginBottom: 4 }}>
                          {strings.totalInvested}
                        </CText>
                        <CText type="B18" color={colors.white}>
                          {formatINR(summary.total_invested)}
                        </CText>
                      </div>
                      <div>
                        <CText type="r12" color="rgba(255,255,255,0.7)" style={{ marginBottom: 4 }}>
                          {strings.currentValue}
                        </CText>
                        <CText type="B18" color={colors.white}>
                          {formatINR(summary.current_value)}
                        </CText>
                      </div>
                      <div>
                        <CText type="r12" color="rgba(255,255,255,0.7)" style={{ marginBottom: 4 }}>
                          {strings.totalPnl}
                        </CText>
                        <div className="flex items-center gap-1.5">
                          <CText type="B18" color={summary.total_pnl >= 0 ? '#4ade80' : '#f87171'}>
                            {summary.total_pnl >= 0 ? '+' : ''}{formatINR(summary.total_pnl)}
                          </CText>
                        </div>
                      </div>
                      <div>
                        <CText type="r12" color="rgba(255,255,255,0.7)" style={{ marginBottom: 4 }}>
                          Returns
                        </CText>
                        <div className="flex items-center gap-1">
                          {React.createElement(
                            summary.total_pnl_pct >= 0 ? (FiTrendingUp as any) : (FiTrendingDown as any),
                            { size: 16, color: summary.total_pnl_pct >= 0 ? '#4ade80' : '#f87171' }
                          )}
                          <CText type="B18" color={summary.total_pnl_pct >= 0 ? '#4ade80' : '#f87171'}>
                            {summary.total_pnl_pct >= 0 ? '+' : ''}{summary.total_pnl_pct.toFixed(2)}%
                          </CText>
                        </div>
                      </div>
                    </div>
                    {uploadInfo && (
                      <div className="mt-4 pt-3 border-t border-white/20">
                        <CText type="r12" color="rgba(255,255,255,0.6)">
                          {summary.holdings_count} {strings.holdings} from {uploadInfo.broker.replace('_', ' ')} &middot; Uploaded {new Date(uploadInfo.uploaded_at).toLocaleDateString('en-IN')}
                        </CText>
                      </div>
                    )}
                  </div>
                )}

                {/* Run Health Check CTA Card */}
                <div
                  className="rounded-xl p-4 mb-5 flex items-center gap-4 border"
                  style={{ backgroundColor: colors.inputBackground, borderColor: colors.borderColor }}
                >
                  <div
                    className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)`,
                    }}
                  >
                    {React.createElement(FiShield as any, { size: 20, color: '#fff' })}
                  </div>
                  <div className="flex-1 min-w-0">
                    <CText type="M14" color={colors.textColor} style={{ fontWeight: 600, marginBottom: 2 }}>
                      Ready for your portfolio health report?
                    </CText>
                    <CText type="r12" color={colors.grayScale500}>
                      AI will score your portfolio, flag risks, and suggest improvements.
                    </CText>
                  </div>
                  <button
                    onClick={handleRunHealthCheck}
                    disabled={healthCheckLoading}
                    className="px-4 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 hover:-translate-y-0.5 flex-shrink-0"
                    style={{
                      background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)`,
                      color: '#fff',
                      boxShadow: colors.shadowMedium,
                      opacity: healthCheckLoading ? 0.7 : 1,
                    }}
                  >
                    {healthCheckLoading ? 'Analyzing...' : 'Run Health Check'}
                  </button>
                </div>

                {/* Holdings List */}
                <CText type="B18" color={colors.textColor} style={{ marginBottom: 12 }}>
                  {strings.holdings} ({holdings.length})
                </CText>
                <div className="space-y-2.5">
                  {holdings.map((h) => (
                    <div
                      key={h.id}
                      className="rounded-xl p-4 border"
                      style={{
                        backgroundColor: colors.inputBackground,
                        borderColor: colors.borderColor,
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <CText type="M14" color={colors.textColor} style={{ fontWeight: 600 }}>
                            {h.trading_symbol}
                          </CText>
                          {h.stock_name && h.stock_name !== h.trading_symbol && (
                            <CText type="r12" color={colors.grayScale500} style={{ marginTop: 1 }}>
                              {h.stock_name}
                            </CText>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right">
                            <CText type="M14" color={colors.textColor} style={{ fontWeight: 600 }}>
                              {formatINR(h.current_value)}
                            </CText>
                            <CText
                              type="r12"
                              color={h.pnl >= 0 ? colors.green : colors.alertColor}
                              style={{ marginTop: 1 }}
                            >
                              {h.pnl >= 0 ? '+' : ''}{formatINR(h.pnl)} ({h.pnl_pct >= 0 ? '+' : ''}{h.pnl_pct.toFixed(2)}%)
                            </CText>
                          </div>
                          {/* Analyze button */}
                          <button
                            onClick={() => handleAnalyzeStock(h)}
                            className="p-2 rounded-lg transition-colors flex-shrink-0"
                            style={{ backgroundColor: colors.primary + '10' }}
                            title="Analyze this stock"
                          >
                            {React.createElement(FiZap as any, { size: 14, color: colors.primary })}
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mt-2.5 pt-2.5 border-t" style={{ borderColor: colors.borderColor }}>
                        <div>
                          <CText type="r10" color={colors.grayScale500}>{strings.qty}</CText>
                          <CText type="M12" color={colors.textColor}>{h.quantity}</CText>
                        </div>
                        <div>
                          <CText type="r10" color={colors.grayScale500}>{strings.avgPrice}</CText>
                          <CText type="M12" color={colors.textColor}>{formatINR(h.average_price)}</CText>
                        </div>
                        <div>
                          <CText type="r10" color={colors.grayScale500}>LTP</CText>
                          <CText type="M12" color={colors.textColor}>{formatINR(h.last_price)}</CText>
                        </div>
                        {h.exchange && (
                          <div>
                            <CText type="r10" color={colors.grayScale500}>Exchange</CText>
                            <CText type="M12" color={colors.textColor}>{h.exchange}</CText>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Health Check Tab */}
            {activeTab === 'healthcheck' && (
              <PortfolioHealthCheck
                result={healthCheckResult}
                loading={healthCheckLoading}
                error={healthCheckError}
                onRunAnalysis={handleRunHealthCheck}
                hasHoldings={holdings.length > 0}
                colors={colors}
              />
            )}

            {/* Stock Analysis Tab */}
            {activeTab === 'analysis' && (
              <PortfolioStockAnalysis
                holding={selectedStock}
                holdings={holdings}
                colors={colors}
                onSelectStock={handleAnalyzeStock}
              />
            )}
          </>
        )}
      </div>

      <PortfolioUploadModal
        isVisible={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSuccess={loadHoldings}
      />
    </div>
  );
};

export default Portfolio;
